import React, { useCallback, useMemo } from 'react'
import { Form, Formik } from 'formik'
import { CircularProgress, Typography } from '@mui/material'
import { array, object, SchemaOf } from 'yup'
import useContentUserQualifications from '@tabeeb/modules/invites/hooks/useContentUserQualifications'
import EditContentUserQualificationsSkeleton from '@tabeeb/modules/invites/components/EditContentUserQualificationsDialog/EditContentUserQualificationsSkeleton'
import { QualificationsAutocomplete } from '@tabeeb/modules/qualifications/components/Fields'
import { UpdateContentUserQualificationsRequest } from '@tabeeb/contracts/main-api/swagger-main-api'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@collaborate-health/uikit'
import { Save } from '@mui/icons-material'
import { FormAutocompleteProps } from '@collaborate-health/forms'
import { UserQualificationResponse } from '@tabeeb/contracts/microservices/swagger-certificate-service'

interface EditContentUserQualificationsProps {
  userIdentityGuid: string
  userId: number
  userName: string
  contentId: number
  qualificationIds: number[]
  open: boolean
  onClose: () => void
  onUpdateSuccess?: (newValues: UpdateContentUserQualificationsRequest) => void
}

interface EditContentUserQualificationsForm {
  Qualifications: FormAutocompleteProps<number, UserQualificationResponse>['options']
}

const schema: SchemaOf<EditContentUserQualificationsForm> = object().shape({
  Qualifications: array().of(object().required()),
})

const EditContentUserQualificationsDialog: React.FC<EditContentUserQualificationsProps> = ({
  userIdentityGuid,
  userId,
  userName,
  contentId,
  qualificationIds,
  open,
  onClose,
  onUpdateSuccess,
}) => {
  const {
    data: qualifications,
    isError,
    isLoading,
    updateMutation,
  } = useContentUserQualifications({
    contentId,
    userId: userIdentityGuid,
    qualificationIds,
    enabled: open,
    onUpdateSuccess,
  })

  const onSubmit = useCallback(
    (values: EditContentUserQualificationsForm) => {
      const request: UpdateContentUserQualificationsRequest = {
        ContentId: contentId,
        UserId: userId,
        QualificationIds: values.Qualifications.map((q) => q.value),
      }
      updateMutation.mutateAsync(request).then(() => {
        onClose()
      })
    },
    [contentId, onClose, updateMutation, userId]
  )

  const initial = useMemo<EditContentUserQualificationsForm>(
    () => ({
      Qualifications:
        qualifications?.map((q) => ({ value: q.QualificationId, name: q.QualificationName, meta: q })) ?? [],
    }),
    [qualifications]
  )

  return (
    <Formik initialValues={initial} validateOnMount validationSchema={schema} onSubmit={onSubmit} enableReinitialize>
      {({ dirty, values, isValid, resetForm }) => {
        return (
          <Dialog
            open={open}
            fullWidth
            maxWidth='sm'
            onClose={onClose}
            TransitionProps={{ unmountOnExit: true, onExited: () => resetForm() }}
          >
            <DialogTitle>Manage required qualifications for {userName}</DialogTitle>
            <DialogContent>
              {isLoading && !qualifications && <EditContentUserQualificationsSkeleton />}
              {isError && <Typography>Failed to load user qualifications</Typography>}
              {!isLoading && (qualifications || []) && (
                <Form id='edit-content-user-qualifications' autoComplete='off'>
                  <QualificationsAutocomplete name='Qualifications' />
                </Form>
              )}
            </DialogContent>
            <DialogActions>
              <Button color='primary' onClick={onClose}>
                Cancel
              </Button>
              <Button
                loading={updateMutation.isPending}
                disabled={!dirty || !isValid}
                variant='contained'
                color='primary'
                type='submit'
                form='edit-content-user-qualifications'
                endIcon={updateMutation.isPending ? <CircularProgress size={18} /> : <Save />}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default EditContentUserQualificationsDialog

